import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { extractErr } from "@/lib/api";
import { PLATFORM_LOGO_URL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Copy, ExternalLink, Loader2, Plus, Search, Trash2, FileText, Share2,
} from "lucide-react";

const GST_RATE = 0.18;
const LOGO = PLATFORM_LOGO_URL;
const SUGGEST = ["Domain", "Hosting", "Development", "Android App", "Website", "SEO", "Maintenance"];
const field = "mt-1 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400";
const card = "rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4";

function money(n) {
  const v = Number(n || 0);
  return Math.round((Number.isFinite(v) ? v : 0) * 100) / 100;
}
function inr(n) {
  return `₹${money(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function line(base, gstOn) {
  const b = money(base);
  const g = gstOn ? money(b * GST_RATE) : 0;
  return { base: b, gst: g, total: money(b + g) };
}
function emptyForm() {
  return {
    gst_enabled: true,
    base_amount: "",
    notes: "",
    customer: { name: "", email: "", phone: "", company: "", address: "", lead_id: "", lead_kind: "" },
    phases: [],
    deliverables: [],
  };
}

function InvoiceEditor({ initial, onCancel, onDeleted }) {
  const [form, setForm] = useState(() => {
    if (!initial) return emptyForm();
    return {
      gst_enabled: initial.gst_enabled !== false,
      base_amount: initial.base_amount ?? "",
      notes: initial.notes || "",
      customer: {
        name: initial.customer?.name || "",
        email: initial.customer?.email || "",
        phone: initial.customer?.phone || "",
        company: initial.customer?.company || "",
        address: initial.customer?.address || "",
        lead_id: initial.customer?.lead_id || "",
        lead_kind: initial.customer?.lead_kind || "",
      },
      phases: (initial.phases || []).map((p) => ({
        id: p.id, title: p.title || "Phase", base_amount: p.base_amount, due_date: p.due_date || "",
      })),
      deliverables: (initial.deliverables || []).map((d) => ({
        id: d.id, title: d.title || "", detail: d.detail || "",
      })),
    };
  });
  const [savedId, setSavedId] = useState(initial?.id || null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [leadQ, setLeadQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/sales/invoices/leads", { params: { q: leadQ } })
        .then(({ data }) => setLeads(data.items || []))
        .catch(() => setLeads([]));
    }, 220);
    return () => clearTimeout(t);
  }, [leadQ]);

  const gstOn = form.gst_enabled;
  const totals = useMemo(() => line(form.base_amount, gstOn), [form.base_amount, gstOn]);
  const phaseRows = useMemo(
    () => (form.phases || []).map((p) => ({ ...p, ...line(p.base_amount, gstOn) })),
    [form.phases, gstOn]
  );

  const setCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const pickLead = (l) => {
    setForm((f) => ({
      ...f,
      customer: {
        ...f.customer,
        name: l.name || f.customer.name,
        email: l.email || f.customer.email,
        phone: l.phone || f.customer.phone,
        lead_id: l.id,
        lead_kind: l.kind,
      },
    }));
    setLeadOpen(false);
    setLeadQ("");
  };
  const payload = () => ({
    gst_enabled: gstOn,
    base_amount: money(form.base_amount),
    notes: form.notes,
    customer: form.customer,
    phases: (form.phases || []).map((p) => ({
      id: p.id, title: p.title, base_amount: money(p.base_amount), due_date: p.due_date || null,
    })),
    deliverables: (form.deliverables || []).map((d) => ({
      id: d.id, title: (d.title || "").trim(), detail: (d.detail || "").trim(),
    })).filter((d) => d.title || d.detail),
  });

  const persist = async () => {
    setSaving(true);
    try {
      if (savedId || initial?.id) {
        const { data } = await api.put(`/sales/invoices/${savedId || initial.id}`, payload());
        toast.success("Invoice saved");
        setSavedId(data.id);
        return data;
      }
      const { data } = await api.post("/sales/invoices", payload());
      toast.success("Invoice created");
      setSavedId(data.id);
      return data;
    } catch (e) {
      toast.error(extractErr(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      let id = savedId || initial?.id;
      if (!id) {
        const created = await persist();
        if (!created) return;
        id = created.id;
      } else {
        const saved = await persist();
        if (!saved) return;
      }
      const { data } = await api.post(`/sales/invoices/${id}/publish`);
      const url = data.share_url || `${window.location.origin}/invoice/${data.share_token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      toast.success("Invoice published — share link copied");
      onCancel(true);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setPublishing(false);
    }
  };

  const remove = async () => {
    const id = savedId || initial?.id;
    if (!id) {
      onCancel(false);
      return;
    }
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/sales/invoices/${id}`);
      toast.success("Invoice deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="invoice-editor">
      <button type="button" onClick={() => onCancel(false)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </button>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{initial?.id ? `Invoice ${initial.number}` : "New invoice"}</h1>
        <p className="text-slate-500 text-sm mt-1">GSTIN {initial?.gstin || "09FDFPB7488N1ZR"} · Powered By Grocode</p>
      </div>

      <div className={card}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-900">GST 18%</div>
            <div className="text-xs text-slate-500">When on, 18% GST is added to the invoice total and to each phase.</div>
          </div>
          <Switch checked={gstOn} onCheckedChange={(v) => setForm((f) => ({ ...f, gst_enabled: v }))} data-testid="inv-gst-switch" />
        </div>
      </div>

      <div className={card}>
        <div className="font-semibold text-slate-900">Customer</div>
        <div className="relative">
          <Label className="text-slate-500">Search leads</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="inv-lead-search"
              className="pl-9 bg-white border-slate-200 text-slate-900"
              placeholder="Search CRM or chatbot leads…"
              value={leadQ}
              onChange={(e) => { setLeadQ(e.target.value); setLeadOpen(true); }}
              onFocus={() => setLeadOpen(true)}
            />
          </div>
          {leadOpen && (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="inv-lead-results">
              <button type="button" className="w-full text-left px-3 py-2 text-xs text-teal-700 hover:bg-slate-50" onClick={() => { setLeadOpen(false); setForm((f) => ({ ...f, customer: { ...f.customer, lead_id: "", lead_kind: "" } })); }}>
                + New customer (enter details below)
              </button>
              {(leads || []).map((l) => (
                <button key={`${l.kind}-${l.id}`} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 border-t border-slate-100" onClick={() => pickLead(l)} data-testid="inv-lead-pick">
                  <div className="text-sm text-slate-900">{l.name || "Unnamed"}</div>
                  <div className="text-[11px] text-slate-500">{l.phone} {l.email} · {l.source}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-slate-500">Name</Label><Input data-testid="inv-cust-name" className={field} value={form.customer.name} onChange={(e) => setCust("name", e.target.value)} /></div>
          <div><Label className="text-slate-500">Email</Label><Input data-testid="inv-cust-email" className={field} value={form.customer.email} onChange={(e) => setCust("email", e.target.value)} /></div>
          <div><Label className="text-slate-500">Phone</Label><Input data-testid="inv-cust-phone" className={field} value={form.customer.phone} onChange={(e) => setCust("phone", e.target.value)} /></div>
          <div><Label className="text-slate-500">Company</Label><Input data-testid="inv-cust-company" className={field} value={form.customer.company} onChange={(e) => setCust("company", e.target.value)} /></div>
        </div>
        <div><Label className="text-slate-500">Address</Label><Textarea data-testid="inv-cust-address" className={field} value={form.customer.address} onChange={(e) => setCust("address", e.target.value)} /></div>
      </div>

      <div className={card}>
        <div className="font-semibold text-slate-900">Amount</div>
        <div>
          <Label className="text-slate-500">Total amount (before GST)</Label>
          <Input data-testid="inv-base-amount" type="number" min="0" step="0.01" className={`${field} font-mono-nums`} value={form.base_amount} onChange={(e) => setForm((f) => ({ ...f, base_amount: e.target.value }))} />
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3"><div className="text-[11px] text-slate-500 uppercase">Taxable</div><div className="font-mono-nums text-slate-900">{inr(totals.base)}</div></div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3"><div className="text-[11px] text-slate-500 uppercase">GST {gstOn ? "18%" : "off"}</div><div className="font-mono-nums text-slate-900">{inr(totals.gst)}</div></div>
          <div className="rounded-xl bg-teal-50 border border-teal-200 p-3"><div className="text-[11px] text-teal-700 uppercase">Grand total</div><div className="font-mono-nums text-teal-800 font-bold">{inr(totals.total)}</div></div>
        </div>
      </div>

      <div className={card}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-slate-900">What we are providing</div>
            <div className="text-xs text-slate-500">Add line items such as Domain, Hosting, Development, Android App.</div>
          </div>
          <Button type="button" variant="outline" data-testid="inv-add-deliverable" className="border-slate-200 bg-white text-slate-800" onClick={() => setForm((f) => ({ ...f, deliverables: [...(f.deliverables || []), { id: crypto.randomUUID(), title: "", detail: "" }] }))}>
            <Plus className="w-4 h-4 mr-1" /> Add item
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SUGGEST.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`inv-suggest-${s.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setForm((f) => {
                if ((f.deliverables || []).some((d) => (d.title || "").toLowerCase() === s.toLowerCase())) return f;
                return { ...f, deliverables: [...(f.deliverables || []), { id: crypto.randomUUID(), title: s, detail: "" }] };
              })}
            >
              + {s}
            </button>
          ))}
        </div>
        {(form.deliverables || []).length === 0 ? (
          <p className="text-sm text-slate-500">No items yet — add what this invoice covers.</p>
        ) : (form.deliverables || []).map((d, i) => (
          <div key={d.id || i} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2" data-testid="inv-deliverable-row">
            <Input className="bg-white border-slate-200 text-slate-900" placeholder="Item (e.g. Domain)" value={d.title} onChange={(e) => setForm((f) => { const deliverables = [...f.deliverables]; deliverables[i] = { ...deliverables[i], title: e.target.value }; return { ...f, deliverables }; })} data-testid="inv-deliverable-title" />
            <Input className="bg-white border-slate-200 text-slate-900" placeholder="What we provide (details)" value={d.detail} onChange={(e) => setForm((f) => { const deliverables = [...f.deliverables]; deliverables[i] = { ...deliverables[i], detail: e.target.value }; return { ...f, deliverables }; })} data-testid="inv-deliverable-detail" />
            <Button type="button" variant="outline" className="border-red-200 text-red-600" onClick={() => setForm((f) => ({ ...f, deliverables: f.deliverables.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <div className={card}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Payment phases</div>
            <div className="text-xs text-slate-500">Optional advances. GST is calculated on each phase.</div>
          </div>
          <Button type="button" variant="outline" data-testid="inv-add-phase" className="border-slate-200 bg-white text-slate-800" onClick={() => setForm((f) => ({ ...f, phases: [...(f.phases || []), { id: crypto.randomUUID(), title: `Phase ${(f.phases || []).length + 1}`, base_amount: "", due_date: "" }] }))}>
            <Plus className="w-4 h-4 mr-1" /> Add phase
          </Button>
        </div>
        {phaseRows.length === 0 ? (
          <p className="text-sm text-slate-500">No phases — customer pays the full invoice outstanding.</p>
        ) : phaseRows.map((p, i) => (
          <div key={p.id || i} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="inv-phase-row">
            <div className="grid sm:grid-cols-4 gap-2">
              <Input className="bg-white border-slate-200 text-slate-900" value={p.title} onChange={(e) => setForm((f) => { const phases = [...f.phases]; phases[i] = { ...phases[i], title: e.target.value }; return { ...f, phases }; })} placeholder="Phase title" />
              <Input type="number" min="0" step="0.01" className="bg-white border-slate-200 text-slate-900 font-mono-nums" value={form.phases[i].base_amount} onChange={(e) => setForm((f) => { const phases = [...f.phases]; phases[i] = { ...phases[i], base_amount: e.target.value }; return { ...f, phases }; })} placeholder="Advance (before GST)" />
              <Input type="date" className="bg-white border-slate-200 text-slate-900" value={form.phases[i].due_date || ""} onChange={(e) => setForm((f) => { const phases = [...f.phases]; phases[i] = { ...phases[i], due_date: e.target.value }; return { ...f, phases }; })} />
              <Button type="button" variant="outline" className="border-red-200 text-red-600" onClick={() => setForm((f) => ({ ...f, phases: f.phases.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="text-xs text-slate-500 font-mono-nums">Phase GST {inr(p.gst)} · Phase total {inr(p.total)}</div>
          </div>
        ))}
      </div>

      <div>
        <Label className="text-slate-500">Notes</Label>
        <Textarea data-testid="inv-notes" className={field} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button data-testid="inv-save" onClick={persist} disabled={saving} className="bg-white border border-slate-200 text-slate-800 hover:bg-slate-50">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save draft
        </Button>
        <Button data-testid="inv-publish" onClick={publish} disabled={publishing} className="bg-teal-600 hover:bg-teal-500 text-white">
          {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />} Publish & share
        </Button>
        {(savedId || initial?.id) ? (
          <Button data-testid="inv-delete" onClick={remove} disabled={deleting} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 ml-auto">
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete invoice
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function SalesInvoice() {
  const nav = useNavigate();
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(id === "new" ? {} : null);

  const load = () => {
    setLoading(true);
    api.get("/sales/invoices").then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(extractErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!id || id === "new") return;
    api.get(`/sales/invoices/${id}`).then(({ data }) => setEditing(data)).catch((e) => toast.error(extractErr(e)));
  }, [id]);

  const deleteInvoice = async (invId) => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/sales/invoices/${invId}`);
      toast.success("Invoice deleted");
      setItems((rows) => rows.filter((x) => x.id !== invId));
      if (id === invId) {
        setEditing(null);
        nav("/sales/invoices");
      }
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const wrap = (inner) => (
    <div className="-m-4 sm:-m-6 lg:-m-8 min-h-[calc(100vh-4rem)] bg-[#EEF2F6] p-4 sm:p-6 lg:p-8 text-slate-900" data-testid="inv-list">
      {inner}
    </div>
  );

  if (editing) {
    return wrap(
      <InvoiceEditor
        initial={editing.id ? editing : null}
        onCancel={(reload) => { setEditing(null); nav("/sales/invoices"); if (reload) load(); }}
        onDeleted={() => { setEditing(null); nav("/sales/invoices"); load(); }}
      />
    );
  }

  return wrap(
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Invoice</h1>
          <p className="text-slate-500 text-sm mt-1">Create GST invoices, share a payment link, and track Cashfree payments.</p>
        </div>
        <Button data-testid="inv-new" className="bg-teal-600 hover:bg-teal-500 text-white" onClick={() => { setEditing({}); nav("/sales/invoices/new"); }}>
          <Plus className="w-4 h-4 mr-1" /> New invoice
        </Button>
      </div>
      {loading ? <p className="text-slate-500 text-sm">Loading…</p> : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-400" /> No invoices yet.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100 bg-white shadow-sm">
          {items.map((inv) => (
            <div key={inv.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="inv-row">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{inv.number} · {inv.customer?.name}</div>
                <div className="text-xs text-slate-500 font-mono-nums">{inr(inv.total)} · due {inr(inv.amount_due)} · {inv.status}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-slate-200 text-slate-800" onClick={() => { setEditing(inv); nav(`/sales/invoices/${inv.id}`); }}>Edit</Button>
                {inv.share_token && inv.status !== "draft" && (
                  <Button size="sm" variant="outline" className="border-slate-200 text-slate-800" onClick={() => window.open(`/invoice/${inv.share_token}`, "_blank")}>
                    <ExternalLink className="w-4 h-4 mr-1" /> View
                  </Button>
                )}
                {inv.share_token && inv.status !== "draft" && (
                  <Button size="sm" variant="outline" className="border-slate-200 text-slate-800" onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/invoice/${inv.share_token}`); toast.success("Link copied"); } catch { toast.error("Copy failed"); } }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid="inv-delete" className="border-red-200 text-red-600" onClick={() => deleteInvoice(inv.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <img src={LOGO} alt="" className="hidden" />
    </div>
  );
}
